import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const NUDGE_MS = 60 * 24 * 60 * 60 * 1000;
const INPUT = "w-full bg-[#141414] border border-[#262626] focus:border-[#CCFF00] focus:outline-none p-2 text-sm text-white";
const BTN = "min-h-[40px] px-4 py-2 text-xs uppercase tracking-wider font-bold bg-[#CCFF00] text-black disabled:opacity-40";
const GHOST = "px-4 py-2 text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-white";

const modeFor = (u) => {
  if (u?.role !== "student") return null;
  if (!u.dupr_prompt_done) return u.dupr_rating ? null : "first";
  const last = Date.parse(u.dupr_prompt_done_at || "");
  return last && Date.now() - last >= NUDGE_MS ? (u.dupr_rating ? "revalidate" : "acquire") : null;
};

const COPY = {
  first: { title: "Got a DUPR rating?", body: "Optional — add it to your profile so coaches can see it.", cta: null, skip: "Skip" },
  revalidate: { title: "Check your DUPR", body: "Your DUPR rating was last updated 60 days ago — is it still accurate?", cta: "Update", skip: "Dismiss" },
  acquire: { title: "Add your DUPR", body: "Would you like to add your DUPR rating to your profile?", cta: "Add", skip: "Dismiss" },
};

// One-time DUPR prompt + 60-day revalidation / acquisition nudge.
export function DuprPrompt() {
  const { user, setUser } = useAuth();
  const [mode] = useState(() => modeFor(user));
  const [editing, setEditing] = useState(mode === "first");
  const [val, setVal] = useState(user?.dupr_rating || "");
  const [busy, setBusy] = useState(false);
  const [closed, setClosed] = useState(false);
  const show = !!mode && !closed && user?.role === "student";
  if (!mode) return null;
  const c = COPY[mode];

  const send = async (skipped) => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/dupr", { dupr_rating: skipped ? "" : val.trim(), skipped });
      setUser({ ...user, dupr_prompt_done: true, dupr_prompt_done_at: new Date().toISOString(), dupr_rating: data.dupr_rating || user.dupr_rating });
      if (!skipped) toast.success("DUPR rating saved to your profile");
    } catch { toast.error("Couldn't save — try again"); return; }
    finally { setBusy(false); }
    setClosed(true);
  };

  return (
    <Dialog open={show} onOpenChange={(o) => { if (!o && !busy) send(true); }}>
      <DialogContent className="bg-[#0F0F0F] border border-[#262626] max-w-md text-[#E5E5E5]" data-testid="dupr-prompt-dialog" data-mode={mode}>
        <DialogHeader>
          <DialogTitle>{c.title}</DialogTitle>
          <DialogDescription className="text-sm text-[#A3A3A3] pt-1" data-testid="dupr-prompt-message">{c.body}</DialogDescription>
        </DialogHeader>
        {editing && (
          <input value={val} onChange={(e) => setVal(e.target.value)} maxLength={40} placeholder="e.g. 3.62" autoFocus data-testid="dupr-prompt-input" className={INPUT} />
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => send(true)} disabled={busy} data-testid="dupr-prompt-skip" className={GHOST}>{c.skip}</button>
          {editing ? (
            <button type="button" onClick={() => send(false)} disabled={busy || !val.trim()} data-testid="dupr-prompt-save" className={BTN}>Save</button>
          ) : (
            <button type="button" onClick={() => setEditing(true)} data-testid="dupr-prompt-edit" className={BTN}>{c.cta}</button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
